import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare, Sun, Moon } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { NAVIGATION_CONFIG } from '../../types/navigation';
import { useThemeStore } from '../../store/themeStore';
import { useChatStore } from '../../store/chatStore';
import { ChangePasswordModal } from '../common/ChangePasswordModal';
import { IconButton } from '../common/IconButton';
import { UserMenu } from './UserMenu';

/**
 * Header component with dark mode toggle
 * Maps to Req 166 AC 3: dark mode support
 * Implements Requirement 197 AC 2: Dark mode with persistence
 * WCAG 2.1 AA compliant with proper aria labels
 */
export const Header: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { darkMode, toggleDarkMode } = useThemeStore();
  const { togglePanel } = useChatStore();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const location = useLocation();

  // Resolve the current route to its nav entry. `startsWith` so detail routes
  // (/recommendations/:id, /anomalies/:id) keep their parent's identity rather
  // than falling back to the product name - the detail page supplies its own
  // heading in the body, and the header should still say where you are.
  const activeNavItem = NAVIGATION_CONFIG.find(
    (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
  );
  const pageTitle = activeNavItem?.label ?? 'CloudAlpha';
  const pageDescription = activeNavItem?.description;

  // Initialize dark mode on mount
  React.useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background">
      <div className="flex h-14 items-center justify-between px-4 sm:px-6">
        {/* Page identity. Every page used to render its own title block in the
            page body, which cost ~60px of vertical space on every screen and
            drifted in size between pages. The title now lives here, once,
            driven by NAVIGATION_CONFIG so it can't disagree with the sidebar
            label. On mobile the description is dropped rather than wrapped -
            a two-line header would push all the content down. */}
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="shrink-0 text-sm font-semibold text-foreground">{pageTitle}</h1>
          {pageDescription && (
            <span className="hidden truncate text-xs text-muted-foreground md:inline">
              {pageDescription}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <IconButton
            onClick={togglePanel}
            aria-label="Open AI chat assistant"
            title="AI Assistant"
          >
            <MessageSquare />
          </IconButton>

          <IconButton
            onClick={toggleDarkMode}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? <Sun /> : <Moon />}
          </IconButton>

          {/* Account menu. Previously three flat items here - the name as
              plain text plus full-size "Change Password" and "Logout"
              buttons - which gave a rare account action the same visual
              weight as the page itself and crowded a 56px header that also
              carries the title, description and two icon buttons. Both
              actions now live behind the avatar, where users expect them. */}
          {user && (
            <div className="pl-1">
              <UserMenu
                user={user}
                onChangePassword={() => setShowChangePassword(true)}
                onLogout={() => logout()}
              />
            </div>
          )}
        </div>
      </div>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </header>
  );
};
